/**
 * Inline assign / reassign — V98-3 review U1 (no dead ends: "Assign owner"
 * and "Reassign" used to link to Team settings).
 *
 * A member picker opens in place. For a reassign the receiver is previewed
 * first (POST /coverage/reassign/preview — owner/admin, ADR-0046 T16): their
 * absences, their load, and any SoD pair the move would break (refused by
 * the server as `receiver_conflicted`). Save is offered only once the
 * preview is back and clean; the reason is required (append-only log).
 *
 * Writes: POST /assignments (assign) · POST /assignments/reassign — owner /
 * admin or the subject's program lead, server-enforced. Callers render this
 * only for owner/admin viewers (the preview is owner/admin-only).
 * Focus returns to the opener on cancel (U12) and moves to the new state on
 * save.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Field, Input, Select } from '@aigovops/design-system';
import { assignSubject, reassignPreview, reassignSubjects, type ReassignPreviewView } from '../../lib/coverage-api';
import { fmtDay, nameOf, type Names } from '../../lib/today-model';
import { errorText } from './shared';

export interface AssignInlineProps {
  slug: string;
  /** The assignment subject (kind from ASSIGNABLE_KINDS). */
  subject: { kind: string; id: string };
  title: string;
  responsibility: string;
  /** Current holder (reassign) or null (assign an unowned item). */
  fromUserId: string | null;
  names: Names;
  /** Element to refocus on cancel. */
  openerId: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}

export function AssignInline({ slug, subject, title, responsibility, fromUserId, names, openerId, onClose, onDone }: AssignInlineProps) {
  const qc = useQueryClient();
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ to?: string; reason?: string }>({});
  const [preview, setPreview] = useState<ReassignPreviewView | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const first = useRef<HTMLSelectElement>(null);
  const reassign = !!fromUserId;
  const idBase = `assign-${subject.kind}-${subject.id}`.replace(/[^a-zA-Z0-9_-]/g, '_');

  useEffect(() => { first.current?.focus(); }, []);
  const close = () => { onClose(); window.setTimeout(() => document.getElementById(openerId)?.focus(), 0); };

  const pick = async (u: string) => {
    setTo(u); setPreview(null); setError(null);
    if (errors.to) setErrors((x) => ({ ...x, to: undefined }));
    if (!u || !reassign) return;
    setPreviewing(true);
    try { setPreview((await reassignPreview(slug, { subjects: [subject], from_user_id: fromUserId!, to_user_id: u, responsibility })).preview); }
    catch (e) { setError(errorText(e, 'The preview could not load — try again before saving.')); }
    finally { setPreviewing(false); }
  };

  const save = async () => {
    if (busy) return;
    const errs: typeof errors = {};
    if (!to) errs.to = 'Choose who takes it.';
    if (reassign && !reason.trim()) errs.reason = 'Say why it moves (kept in the log).';
    setErrors(errs);
    if (errs.to || errs.reason) return;
    if (reassign && (!preview || preview.conflicts.length)) return;
    setBusy(true); setError(null);
    try {
      if (reassign) await reassignSubjects(slug, { subjects: [subject], from_user_id: fromUserId!, to_user_id: to, responsibility, reason: reason.trim() });
      else await assignSubject(slug, { subject_kind: subject.kind, subject_id: subject.id, responsibility, user_id: to });
      await Promise.all([qc.invalidateQueries({ queryKey: ['blockers', slug] }), qc.invalidateQueries({ queryKey: ['today', slug] })]);
      onDone(`${reassign ? 'Reassigned' : 'Assigned'} · ${title} → ${nameOf(names, to)}`);
    } catch (e) { setError(errorText(e, 'The assignment could not be saved.')); }
    finally { setBusy(false); }
  };

  const away = preview?.receiver.absences.filter((a) => a.state !== 'declined' && a.state !== 'ended') ?? [];
  const load = preview?.receiver.load;
  const blocked = !!preview && preview.conflicts.length > 0;
  return (
    <div className="gp-inline-assign" role="group" aria-labelledby={`${idBase}-h`}>
      <p id={`${idBase}-h`} className="gp-inline-assign-h">{reassign ? `Reassign from ${nameOf(names, fromUserId)}` : 'Assign an owner'}</p>
      <Field id={`${idBase}-to`} label={reassign ? 'Move to' : 'Owner'} required error={errors.to}>
        <Select ref={first} value={to} onChange={(e) => void pick(e.currentTarget.value)}>
          <option value="">Choose…</option>
          {[...names].filter(([u]) => u !== fromUserId).map(([u, n]) => <option key={u} value={u}>{n}</option>)}
        </Select>
      </Field>
      {reassign ? (
        <Field id={`${idBase}-reason`} label="Reason" required hint="Kept in the assignment log" error={errors.reason}>
          <Input value={reason} maxLength={2000} onChange={(e) => { setReason(e.currentTarget.value); if (errors.reason) setErrors((x) => ({ ...x, reason: undefined })); }} />
        </Field>
      ) : null}
      <div aria-live="polite" className="gp-inline-assign-preview">
        {previewing ? <span className="gp-dim">Checking availability…</span> : null}
        {preview ? (
          <span data-part="reassign-preview">
            {blocked
              ? `${nameOf(names, to)} can’t take it: they’re in a conflicting pair on this item.`
              : `${nameOf(names, to)} · ${load!.open_work_items} open, ${load!.signatures_waiting} to sign${away.length ? ` · away ${away.map((a) => `${fmtDay(a.starts_on)} – ${fmtDay(a.ends_on)}`).join(', ')}` : ' · available'}`}
          </span>
        ) : null}
      </div>
      {error ? <p className="gp-field-err" role="alert">{error}</p> : null}
      <div className="gp-actions">
        <Button compact onClick={close}>Cancel</Button>
        <Button compact variant="secondary" aria-disabled={busy || previewing || blocked || (reassign && !!to && !preview) || undefined} onClick={() => void save()}>
          {busy ? 'Saving…' : reassign ? 'Reassign' : 'Assign'}
        </Button>
      </div>
    </div>
  );
}
